import { useState, useMemo, useCallback, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { plural } from '@lingui/core/macro';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@pantry/ui';
import { cn } from '@pantry/ui';
import { XMarkIcon, CheckCircleIcon, ArchiveBoxIcon } from '@heroicons/react/24/outline';
import {
  type ItemInstance,
  type ItemWithInstanceCounts,
  UNIT_DISPLAY_LABELS,
  formatQuantityDisplay,
} from '@pantry/api-client';
import { type Product, parseLocalDate, getDaysUntilExpiry, formatExpiryDate } from '@pantry/shared';
import { useResolveProductName } from '../../hooks/useProductLanguage';

interface InstancePickerSheetProps {
  /** Whether the sheet is open */
  isOpen: boolean;
  /** Callback when sheet is closed */
  onClose: () => void;
  /** The item these instances belong to */
  item: ItemWithInstanceCounts | null;
  /** Active instances to select from (already FIFO sorted) */
  instances: ItemInstance[];
  /**
   * Callback when an instance is selected.
   * Passes the selected instance.
   */
  onSelectInstance: (instance: ItemInstance) => void;
}

/**
 * Get expiry status and styling.
 */
function getExpiryStatus(
  expiryDate: string | null,
  t?: ReturnType<typeof useLingui>['t'],
  locale?: string
): {
  status: 'expired' | 'expiring-soon' | 'fresh' | 'none';
  label: string | null;
  daysUntil: number | null;
} {
  if (!expiryDate) return { status: 'none', label: null, daysUntil: null };

  const diffDays = getDaysUntilExpiry(expiryDate);
  const formattedDate = formatExpiryDate(expiryDate, locale);

  if (diffDays < 0) {
    return {
      status: 'expired',
      label: t ? t`Expired ${formattedDate}` : `Expired ${formattedDate}`,
      daysUntil: diffDays,
    };
  }
  if (diffDays === 0) {
    return { status: 'expiring-soon', label: t ? t`Expires today` : 'Expires today', daysUntil: 0 };
  }
  if (diffDays <= 3) {
    return {
      status: 'expiring-soon',
      label: t
        ? t`Expires in ${plural(diffDays, { one: '# day', other: '# days' })}`
        : `Expires in ${diffDays} day${diffDays > 1 ? 's' : ''}`,
      daysUntil: diffDays,
    };
  }
  return { status: 'fresh', label: formatExpiryDate(expiryDate, locale), daysUntil: diffDays };
}

/**
 * Slide-up sheet for selecting a specific instance when multiple exist.
 * Shows FIFO-sorted instances with pre-selection of earliest expiry.
 * Used after InventoryPickerSheet when an item has multiple active instances.
 */
export function InstancePickerSheet({
  isOpen,
  onClose,
  item,
  instances,
  onSelectInstance,
}: InstancePickerSheetProps) {
  const { t, i18n } = useLingui();
  // Pre-select the first instance (earliest expiry due to FIFO sort)
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const resolveProductName = useResolveProductName();

  // Reset selection when sheet opens with new instances
  useEffect(() => {
    if (isOpen && instances.length > 0) {
      // Pre-select the first instance (FIFO - earliest expiry)
      setSelectedInstanceId(instances[0].id);
    } else if (!isOpen) {
      setSelectedInstanceId(null);
    }
  }, [isOpen, instances]);

  const handleClose = useCallback(() => {
    setSelectedInstanceId(null);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    const selected = instances.find((i) => i.id === selectedInstanceId);
    if (selected) {
      onSelectInstance(selected);
      handleClose();
    }
  }, [instances, selectedInstanceId, onSelectInstance, handleClose]);

  // Get item info
  const itemName =
    item?.customName ||
    (item?.product ? resolveProductName(item.product as Product) : null) ||
    t`Unnamed Item`;
  const itemBrand = item?.product?.brand;
  const itemImage = item?.product?.imageUrl;
  const itemUnit = item?.unit || 'item';

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Sheet */}
      <div className="absolute bottom-0 left-0 right-0 bg-background rounded-t-2xl shadow-xl animate-in slide-in-from-bottom duration-300 max-h-[70vh] flex flex-col">
        <Card className="border-0 shadow-none flex flex-col flex-1 min-h-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 flex-shrink-0">
            <div className="flex items-center gap-3">
              {/* Product image */}
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg overflow-hidden bg-muted">
                {itemImage ? (
                  <img src={itemImage} alt={itemName} className="h-full w-full object-cover" />
                ) : (
                  <ArchiveBoxIcon className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
              <div>
                <CardTitle className="text-lg">{itemName}</CardTitle>
                {itemBrand && <p className="text-sm text-muted-foreground">{itemBrand}</p>}
              </div>
            </div>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleClose}>
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </CardHeader>

          <CardContent className="flex flex-col flex-1 min-h-0 pb-6 space-y-4">
            {/* Info text */}
            <p className="text-sm text-muted-foreground">
              <Trans>Multiple entries found. Select which one to use:</Trans>
            </p>

            {/* Recommended banner */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-2 text-sm flex items-center gap-2">
              <CheckCircleIcon className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="text-primary">
                <Trans>First entry is recommended (FIFO - use oldest first)</Trans>
              </span>
            </div>

            {/* Instance list */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {instances.map((instance, index) => {
                const expiryInfo = getExpiryStatus(instance.expiryDate, t, i18n.locale);
                const isSelected = instance.id === selectedInstanceId;
                const isRecommended = index === 0;

                return (
                  <button
                    key={instance.id}
                    onClick={() => setSelectedInstanceId(instance.id)}
                    className={cn(
                      'flex w-full items-center gap-3 p-3 rounded-lg text-left transition-colors border-2',
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-transparent hover:bg-muted/50',
                      isRecommended && !isSelected && 'border-dashed border-primary/30'
                    )}
                  >
                    {/* Radio indicator */}
                    <div
                      className={cn(
                        'h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                        isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/50'
                      )}
                    >
                      {isSelected && <div className="h-2 w-2 rounded-full bg-white" />}
                    </div>

                    {/* Instance info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {formatQuantityDisplay(instance.quantity, itemUnit)}{' '}
                          {UNIT_DISPLAY_LABELS[itemUnit]}
                        </span>
                        {isRecommended && (
                          <span className="inline-flex items-center rounded-full bg-primary/20 px-1.5 py-0.5 text-xs text-primary">
                            <Trans>Recommended</Trans>
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {/* Expiry status */}
                        {expiryInfo.label && (
                          <span
                            className={cn(
                              'inline-flex items-center rounded-full px-1.5 py-0.5 text-xs',
                              expiryInfo.status === 'expired' && 'bg-expired/20 text-expired',
                              expiryInfo.status === 'expiring-soon' &&
                                'bg-expiring-soon/20 text-expiring-soon',
                              expiryInfo.status === 'fresh' && 'bg-muted text-muted-foreground'
                            )}
                          >
                            {expiryInfo.label}
                          </span>
                        )}

                        {/* Purchase date if available */}
                        {instance.purchaseDate && (
                          <span className="text-xs text-muted-foreground">
                            <Trans>
                              Purchased {formatExpiryDate(instance.purchaseDate, i18n.locale)}
                            </Trans>
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Confirm button */}
            <Button
              onClick={handleConfirm}
              disabled={!selectedInstanceId}
              className="w-full"
              size="lg"
            >
              <Trans>Select Entry</Trans>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
